import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import './ChangeLog.css';

interface ChangelogEntry {
  date: string;
  version?: string;
  type: 'feature' | 'methodology' | 'improvement' | 'fix';
  title: string;
  description: string;
  highlights?: string[];
}

const changelogData: ChangelogEntry[] = [
  {
    date: '2026-01-21',
    version: '1.2.0',
    type: 'feature',
    title: 'New Overview Dashboard with Livability & Climate Equity Index',
    description: 'Introduced comprehensive Overview dashboard page featuring the first draft of the Livability & Climate Equity Index, new data visualizations, enhanced VMT backcasting options, improved energy data extrapolation, and public access to Policy Tool.',
    highlights: [
      'New Overview dashboard page providing comprehensive climate action data overview',
      'Livability & Climate Equity Index with Burdens and Benefits indicators',
      'New charts: Cumulative Solar Capacity, Waste Tonnage, Landfill Emissions, Income by Race, Housing Burden',
      'Smart titles on Electricity and Natural Gas charts showing percent change since 2005',
      'Enhanced VMT backcasting with multiple methods: AADT, EMFAC2025, EMFAC Fleet, EMFAC2021 Fleet, Population, and Combined',
      'New trend-based extrapolation methodology for electricity and natural gas data',
      'Livability Index indicators include GHG emissions, pollution, health, transportation, built environment, equity, socioeconomic, mobility, and community resources',
      'Policy Tool now available to all users (previously restricted to developers and administrators)',
      'Navigation guard to ensure Overview page fully loads before navigating to other pages'
    ]
  },
  {
    date: '2026-01-17',
    version: '1.1.2',
    type: 'improvement',
    title: 'Code Maintenance and Data Processing Improvements',
    description: 'Improved energy data processing scripts, resolved merge conflicts, and added comprehensive cleanup documentation for Python scripts and data files.',
    highlights: [
      'Resolved merge conflicts in energy_ng_places.py with improved Path type handling and Supabase integration',
      'Added Python folder cleanup recommendations document identifying files for removal',
      'Updated offroad.py and energy processing scripts with latest improvements',
      'Cleaned up temporary and obsolete files from codebase',
      'Improved code organization and documentation structure'
    ]
  },
  {
    date: '2025-01-08',
    version: '1.1.1',
    type: 'improvement',
    title: 'VMT Backcasting Enhancements and Data Cleanup',
    description: 'Enhanced VMT backcasting methodology with Population-based backcasting option and improved EMFAC Fleet integration. Removed unused data files and streamlined data processing.',
    highlights: [
      'Added Population-based backcasting option for 2005-2014 and 2015-2022 VMT projections',
      'Simplified EMFAC Fleet backcasting to always use EMFAC2025 Fleet data',
      'Added Combined backcasting method averaging multiple data sources',
      'Updated EMFAC County VMT chart to compare EMFAC2021 and EMFAC2025 LDV VMT (2000-2050)',
      'Cleaned up unused data files (CSV and ZIP archives)',
      'Removed obsolete troubleshooting and comparison scripts'
    ]
  },
  {
    date: '2025-01-06',
    version: '1.1.0',
    type: 'methodology',
    title: 'Migration to EMFAC2025 Data',
    description: 'Upgraded transportation emissions calculations from EMFAC2021 to EMFAC2025, providing the latest vehicle fleet data, emissions factors, and forecasting projections. EMFAC2025 includes updated vehicle categories, improved emissions modeling, and extended projection years.',
    highlights: [
      'Migrated from EMFAC2021 to EMFAC2025 county-level data',
      'Updated emfac2025_county and emfac2025_fleet tables with latest emissions factors',
      'Enhanced vehicle category classifications and fuel type modeling',
      'Improved forecasting accuracy with updated EMFAC2025 projection methodology',
      'Extended data coverage with latest vehicle fleet composition data'
    ]
  },
  {
    date: '2025-01-06',
    version: '1.0.10',
    type: 'methodology',
    title: 'Updated EMFAC County Bus Data Aggregation',
    description: 'Fixed bus category (BS) data aggregation to properly include all bus subcategories (UBUS, All Other Buses, OBUS) for accurate transportation emissions calculations.',
    highlights: [
      'Corrected BS category aggregation in emfac2021_county table',
      'Added proper groupby aggregation for vehicle subcategories',
      'Extended data coverage to include years 2043-2050'
    ]
  },
  {
    date: '2025-12-30',
    version: '1.0.9',
    type: 'feature',
    title: 'Community Comments and Discussion Features',
    description: 'Added comprehensive community engagement features allowing users to post climate solutions, ask questions, and engage in discussions.',
    highlights: [
      'Community discussion board with threaded comments',
      'Safety controls for content moderation',
      'User profiles with organization and location information',
      'Email notifications for community interactions'
    ]
  },
  {
    date: '2025-12-20',
    version: '1.0.8',
    type: 'methodology',
    title: 'Enhanced Natural Gas Data Processing',
    description: 'Improved natural gas emissions calculations with better county-level data extrapolation and synchronization from source tables.',
    highlights: [
      'Updated natural gas county data extrapolation methodology',
      'Improved data synchronization from source tables',
      'Enhanced accuracy in building emissions calculations'
    ]
  },
  {
    date: '2025-12-15',
    version: '1.0.7',
    type: 'feature',
    title: 'Policy Tool for Developers',
    description: 'Introduced Policy Tool interface for developers and administrators to test and analyze climate policy scenarios. The Policy Tool is only available to authenticated users.',
    highlights: [
      'Interactive policy scenario modeling',
      'Real-time emissions impact calculations',
      'Role-based access control for policy tool',
      'Available exclusively to authenticated users'
    ]
  },
  {
    date: '2025-12-10',
    version: '1.0.6',
    type: 'methodology',
    title: 'Electricity Carbon Intensity Updates',
    description: 'Updated California electricity carbon intensity calculations with improved county-level data and forecasting methodology.',
    highlights: [
      'Enhanced electricity carbon intensity calculations',
      'Improved county-level electricity data processing',
      'Updated forecasting ratios for future years'
    ]
  },
  {
    date: '2025-12-05',
    version: '1.0.5',
    type: 'improvement',
    title: 'User Management and Role System',
    description: 'Implemented comprehensive user management system with role-based access control and user profile management.',
    highlights: [
      'User role management (developer, super_admin, etc.)',
      'User profile editing and management interface',
      'Enhanced authentication and authorization system'
    ]
  },
  {
    date: '2025-11-25',
    version: '1.0.4',
    type: 'methodology',
    title: 'Transportation VMT and Emissions Refinements',
    description: 'Refined vehicle miles traveled (VMT) calculations and light-duty vehicle (LDV) emissions methodology for improved accuracy.',
    highlights: [
      'Updated LDV VMT calculation methodology',
      'Improved emissions factors for transportation sector',
      'Enhanced data validation and quality checks'
    ]
  },
  {
    date: '2025-11-20',
    version: '1.0.3',
    type: 'feature',
    title: 'Developers\' Log and Task Management',
    description: 'Added Developers\' Log feature for internal task tracking and project management with email notifications.',
    highlights: [
      'Task creation and assignment system',
      'Email notifications for task assignments',
      'Developer profile pages and task tracking'
    ]
  },
  {
    date: '2025-11-15',
    version: '1.0.2',
    type: 'methodology',
    title: 'Waste Emissions Calculation Updates',
    description: 'Improved waste sector emissions calculations with updated methodology and data sources.',
    highlights: [
      'Enhanced waste emissions calculation methodology',
      'Updated waste data processing pipeline',
      'Improved accuracy in waste sector GHG inventory'
    ]
  },
  {
    date: '2025-11-10',
    version: '1.0.1',
    type: 'improvement',
    title: 'Enhanced Data Visualization and Charts',
    description: 'Improved chart visualizations and data presentation across all sectors with better interactivity and design.',
    highlights: [
      'Enhanced chart.js visualizations',
      'Improved map interactions and data display',
      'Better responsive design for mobile devices'
    ]
  },
  {
    date: '2025-11-05',
    version: '1.0.0',
    type: 'feature',
    title: 'Initial Launch of ClimatePlans.org',
    description: 'Official launch of ClimatePlans.org platform with comprehensive GHG inventory, transportation, buildings, waste, and people data visualization.',
    highlights: [
      'Complete GHG inventory dashboard',
      'Transportation sector analysis with VMT and emissions',
      'Buildings and energy sector data visualization',
      'Waste sector emissions tracking',
      'People and demographics data',
      'Interactive maps and charts',
      'Location-based data filtering',
      'User authentication and profiles'
    ]
  }
];

const ChangeLog: React.FC = () => {
  const { user, loading } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <div className="changelog-container">
        <div className="changelog-loading">
          <div className="changelog-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Require authentication
  if (!user) {
    return <Navigate to="/" replace />;
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return '✨';
      case 'methodology':
        return '📊';
      case 'improvement':
        return '🔧';
      case 'fix':
        return '🐛';
      default:
        return '📝';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'feature':
        return 'New Feature';
      case 'methodology':
        return 'Methodology Update';
      case 'improvement':
        return 'Improvement';
      case 'fix':
        return 'Bug Fix';
      default:
        return 'Update';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="changelog-container">
      <div className="changelog-header">
        <h1>Change Log</h1>
        <p className="changelog-subtitle">
          A summary of major updates, methodology changes, and new features to ClimatePlans.org
        </p>
      </div>

      <div className="changelog-timeline">
        {changelogData.map((entry, index) => (
          <div key={index} className="changelog-entry">
            <div className="changelog-entry-date">
              <div className="changelog-date-badge">
                {formatDate(entry.date)}
              </div>
              {entry.version && (
                <div className="changelog-version">v{entry.version}</div>
              )}
            </div>

            <div className="changelog-entry-content">
              <div className="changelog-entry-header">
                <span className="changelog-type-icon">{getTypeIcon(entry.type)}</span>
                <span className="changelog-type-label">{getTypeLabel(entry.type)}</span>
                <h2 className="changelog-entry-title">{entry.title}</h2>
              </div>

              <p className="changelog-entry-description">{entry.description}</p>

              {entry.highlights && entry.highlights.length > 0 && (
                <ul className="changelog-highlights">
                  {entry.highlights.map((highlight, idx) => (
                    <li key={idx}>{highlight}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="changelog-footer">
        <p>
          For detailed technical documentation and development notes, see the{' '}
          <a href="/developers-log" className="changelog-link">Developers' Log</a>.
        </p>
      </div>
    </div>
  );
};

export default ChangeLog;

